import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { notification, Space, Select } from 'antd';
import { FaArrowLeft } from 'react-icons/fa';
import { connect } from 'react-redux';
import * as NotificationActions from '../store/slices/notificationConfig/notificationConfig';
import * as UserActions from '../store/slices/user/user';
import { FormInput } from '../components/Basic/FormInput';
import Button from '../components/Basic/button';
import style from '../pages/RoleManage/RoleManage.module.css';
import styles from './Form.module.css';

interface FormValues {
  userId: number;
  email: string | null;
  type: string;
}

const schema = yup.object({
  userId: yup.number().required('User ID is required').positive('User ID must be positive'),
  email: yup.string().email('Invalid email format').nullable().default(null),
  type: yup.string().required('Type is required'),
});

interface Api {
  Role_Red: any;
  User_Red: any;
  createConfig: (payload: any) => unknown;
  updateConfig: (id: number, payload: any) => unknown;
  getConfigById: (id: number) => unknown;
  getAllUser: (params: { page?: number; size?: number; sort?: string[] }) => unknown;
}

interface Props {
  Back: () => void;
  initialValues?: any;
  isEdit?: boolean;
  configId?: number | string;
}

type NotificationConfigFormProps = Api & Props;

const NotificationConfigForm: React.FC<NotificationConfigFormProps> = ({
  Back,
  Role_Red,
  User_Red,
  createConfig,
  updateConfig,
  getConfigById,
  getAllUser,
  initialValues,
  isEdit = false,
  configId,
}) => {
  const [api, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('announce');
  const [users, setUsers] = useState<any[]>([]);


  useEffect(() => {
    getAllUser({ page: 0, size: 1000 });
  }, [getAllUser]);

  useEffect(() => {
    const userData = User_Red?.data?.content;
    console.log('User_Red:', User_Red);
    console.log('userData:', userData);
    if (userData && Array.isArray(userData)) {
      setUsers(userData);
    }
  }, [User_Red]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: { userId: 0, email: null, type: 'announce' },
  });

  useEffect(() => {
    if (isEdit && initialValues) {
      setValue('userId', initialValues.userId || 0);
      setValue('email', initialValues.email || '');
      setValue('type', initialValues.type || 'announce');
      setSelectedType(initialValues.type || 'announce');
    }
  }, [initialValues, isEdit, setValue]);

  const onSubmit = handleSubmit(async (values) => {
    setLoading(true);
    const payload = {
      userId: values.userId,
      email: values.email || null,
      type: values.type,
    };

    try {
      if (isEdit && configId) {
        await updateConfig(Number(configId), payload);
        api.success({ message: 'Configuration updated successfully' });
      } else {
        await createConfig(payload);
        api.success({ message: 'Configuration created successfully' });
      }
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      api.error({
        message: isEdit ? 'Failed to update configuration' : 'Failed to create configuration',
        description: err?.message || String(err),
      });
    } finally {
      setLoading(false);
    }
  });

  return (
    <>
      {contextHolder}

      <div className={style.Route}>
        <h5>
          <FaArrowLeft onClick={Back} style={{ cursor: 'pointer', marginRight: 8 }} />
          Notification Configuration / {isEdit ? 'Edit' : 'Create'} Configuration
        </h5>

        <Space>
          <Button
            Text="Save"
            buttonClass={style.createBtn}
            onClick={onSubmit}
            Disable={loading || isSubmitting}
          />
          <Button Text="Cancel" onClick={Back} Disable={loading || isSubmitting} />
        </Space>
      </div>

      <div className={style.container}>
        <Space direction="vertical" style={{ width: '100%', marginBottom: 32 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div className={styles.field} style={{ minWidth: 280 }}>
              <label className={style.Label}>User</label>
              <Controller
                name="userId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    showSearch
                    placeholder="Select a user"
                    loading={!users.length}
                    style={{ width: '100%', height: 40 }}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={users.map((user) => ({
                      value: user.userId,
                      label: `${user.userName} (ID: ${user.userId})`,
                    }))}
                  />
                )}
              />
              {errors.userId && <span className={style.Error}>{errors.userId.message}</span>}
            </div>

            <div className={styles.field} style={{ minWidth: 340 }}>
              <FormInput
                Label="Email"
                placeholder="Enter email (optional)"
                name="email"
                control={control}
                errors={errors}
                classInput={style.Input}
                className={style.inputContainer}
                labelClass={style.Label}
                classError={style.Error}
              />
            </div>

            <div className={styles.field} style={{ minWidth: 280 }}>
              <label className={style.Label}>Type</label>
              <Select
                value={selectedType}
                onChange={(value) => {
                  setSelectedType(value);
                  setValue('type', value);
                }}
                style={{ width: '100%', height: 40 }}
                options={[
                  { value: 'announce', label: 'Announce' },
                  // { value: 'alert', label: 'Alert' },
                  // { value: 'reminder', label: 'Reminder' },
                ]}
              />
            </div>
          </div>
        </Space>
      </div>
    </>
  );
};

function mapStateToProps(state: any) {
  return { 
    Role_Red: state.NotificationConfig_Red,
    User_Red: state.User_Red 
  };
}

const allActions = { ...NotificationActions, ...UserActions };

export default connect(mapStateToProps, allActions)(NotificationConfigForm);
